import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import { Progress, ResponseErrorPanel } from "@backstage/core-components";
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import { getCompoundEntityRef } from "@backstage/catalog-model";
import { useEntity } from "@backstage/plugin-catalog-react";
import { techInsightsApiRef } from "@backstage-community/plugin-tech-insights-react";
import {
  categoryAnchorOf,
  GITHUB_SLUG_ANNOTATION,
  TechInsightsCheckResult,
} from "@internal/plugin-front-door-common";
import {
  buildModel,
  CategorySection,
  CheckRow,
  FactRows,
  NotAutomatedRow,
  relativeTime,
  ServiceScorecardModel,
} from "./serviceScorecardModel";
import { categoryId } from "../categoryId";
import { useStandards } from "../standards";
import { loadDeclaredRetrievers } from "../facts";
import { EnforcementChips } from "../EnforcementChips";
import { FixAnchor } from "../FixAnchor";
import { PassBar } from "../PassBar";
import { VisuallyHidden } from "../../status/VisuallyHidden";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  subtle: { color: theme.palette.text.secondary },
  section: { borderTop: `1px solid ${theme.palette.divider}` },
  sectionToggle: {
    width: "100%",
    justifyContent: "flex-start",
    textTransform: "none",
    padding: theme.spacing(1.5, 1),
  },
  sectionLabel: {
    display: "grid",
    gridTemplateColumns: "24px minmax(110px, 160px) minmax(80px, 200px) auto",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    textAlign: "left",
  },
  sectionBody: { padding: theme.spacing(0, 1, 2, 5) },
  row: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  rowIcon: { marginTop: 2, flexShrink: 0 },
  failIcon: { color: theme.palette.error.main },
  passIcon: { color: theme.palette.success.main },
  noDataIcon: { color: theme.palette.text.secondary },
  rowTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  passToggle: { textTransform: "none", marginLeft: theme.spacing(-1) },
  list: { listStyle: "none", margin: 0, padding: 0 },
  notAutomatedHeading: { marginTop: theme.spacing(2) },
}));

/** A check's description, with how it is measured on its own line. */
function CheckDescription({ row }: { row: CheckRow }) {
  if (!row.measurement) return <>{row.description}</>;
  return (
    <>
      <span>{row.description}</span>
      <br />
      <span>How it's measured: {row.measurement}</span>
    </>
  );
}

function FailingRow({ row }: { row: CheckRow }) {
  const classes = useStyles();
  return (
    <li className={classes.row} data-testid={`row-${row.id}`}>
      <CancelIcon
        fontSize="small"
        className={`${classes.rowIcon} ${classes.failIcon}`}
        aria-hidden
      />
      <Box>
        <div className={classes.rowTitle}>
          <VisuallyHidden>Failing:</VisuallyHidden>
          <Tooltip title={<CheckDescription row={row} />} placement="top-start">
            <Typography variant="body1" component="span">
              {row.name}
            </Typography>
          </Tooltip>
          <EnforcementChips check={row} />
        </div>
        {row.evidence.length > 0 && (
          <Typography variant="body2">{row.evidence.join(" · ")}</Typography>
        )}
        {(row.howToFix || row.fixSteps || row.fix) && (
          <Typography variant="body2" className={classes.subtle}>
            <strong>Fix:</strong> {row.howToFix}
            {row.fixSteps && <> {row.fixSteps}</>}{" "}
            {row.fix && <FixAnchor fix={row.fix} />}
          </Typography>
        )}
      </Box>
    </li>
  );
}

function NoDataRow({ row }: { row: CheckRow }) {
  const classes = useStyles();
  return (
    <li className={classes.row} data-testid={`row-${row.id}`}>
      <HelpOutlineIcon
        fontSize="small"
        className={`${classes.rowIcon} ${classes.noDataIcon}`}
        aria-hidden
      />
      <Box>
        <div className={classes.rowTitle}>
          <Typography variant="body1" component="span">
            {row.name}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={row.notApplicableLabel ?? "No data yet"}
          />
        </div>
        <Typography variant="body2" className={classes.subtle}>
          {row.noDataHint}
        </Typography>
      </Box>
    </li>
  );
}

function PassingRow({ row }: { row: CheckRow }) {
  const classes = useStyles();
  return (
    <li className={classes.row} data-testid={`row-${row.id}`}>
      <CheckCircleIcon
        fontSize="small"
        className={`${classes.rowIcon} ${classes.passIcon}`}
        aria-hidden
      />
      <VisuallyHidden>Passing:</VisuallyHidden>
      <Tooltip title={<CheckDescription row={row} />} placement="top-start">
        <Typography variant="body2" component="span">
          {row.name}
        </Typography>
      </Tooltip>
      {row.detail?.length ? (
        <Typography variant="body2" component="span" className={classes.subtle}>
          {row.detail.join(" · ")}
        </Typography>
      ) : null}
      <EnforcementChips check={row} />
    </li>
  );
}

/** Registry controls in the section's category that no check measures; never scored. */
function NotAutomatedList(props: {
  category: string;
  rows: NotAutomatedRow[];
  id: string;
}) {
  const classes = useStyles();
  const { category, rows, id } = props;
  const headingId = `${id}-not-automated`;
  return (
    <>
      <Typography
        id={headingId}
        variant="subtitle2"
        component="h3"
        className={classes.notAutomatedHeading}
      >
        <VisuallyHidden>{category}: </VisuallyHidden>Self-attested, not
        automated
      </Typography>
      <ul aria-labelledby={headingId} className={classes.list}>
        {rows.map((row) => (
          <li
            key={row.id}
            className={classes.row}
            data-testid={`not-automated-${row.id}`}
          >
            <RadioButtonUncheckedIcon
              fontSize="small"
              className={`${classes.rowIcon} ${classes.noDataIcon}`}
              aria-hidden
            />
            <Box>
              <Typography variant="body1" component="span">
                {row.name}
              </Typography>
              {row.statusLabel && (
                <>
                  {" "}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={row.statusLabel}
                  />
                </>
              )}
              <Typography variant="body2">{row.statement}</Typography>
              <Typography variant="body2" className={classes.subtle}>
                <strong>Fix:</strong> {row.howToFix}{" "}
                {row.fix && <FixAnchor fix={row.fix} />}
              </Typography>
            </Box>
          </li>
        ))}
      </ul>
    </>
  );
}

const sectionId = (category: string) =>
  categoryId("scorecard-section", category);

function Section(props: {
  section: CategorySection;
  expanded: boolean;
  onToggle: () => void;
}) {
  const classes = useStyles();
  const { section, expanded, onToggle } = props;
  const [showPassing, setShowPassing] = useState(false);
  const id = sectionId(section.category);
  const failing = section.rows.filter((r) => r.status === "fail");
  const noData = section.rows.filter((r) => r.status === "noData");
  const passing = section.rows.filter((r) => r.status === "pass");
  const allNoData = section.scored === 0;
  const onlyNotAutomated = section.rows.length === 0;
  let unscoredSummary = "no data";
  if (onlyNotAutomated) {
    unscoredSummary = `${section.notAutomated.length} self-attested, not automated`;
  } else if (section.noDataSummary) {
    unscoredSummary = `no data: ${section.noDataSummary}`;
  }

  let icon = expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />;
  if (allNoData && !expanded)
    icon = <HelpOutlineIcon className={classes.noDataIcon} />;

  return (
    <div className={classes.section}>
      <Button
        id={`${id}-toggle`}
        // The focus ripple crashes when a hash link focuses the toggle on load.
        focusRipple={false}
        className={classes.sectionToggle}
        aria-expanded={expanded}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className={classes.sectionLabel}>
          {icon}
          <Typography variant="subtitle1" component="span">
            <strong>{section.category}</strong>
          </Typography>
          {allNoData ? (
            <Typography
              variant="body2"
              component="span"
              className={classes.subtle}
            >
              {unscoredSummary}
            </Typography>
          ) : (
            <PassBar
              tally={{ passed: section.passed, total: section.scored }}
              label={`${section.category}: ${section.passed} of ${section.scored} passing`}
            />
          )}
          {!allNoData && (
            <Typography variant="body2" component="span">
              {section.passed}/{section.scored}
              {section.failed > 0 && ` · ${section.failed} failing`}
              {section.noData > 0 && ` · ${section.noData} no data`}
            </Typography>
          )}
        </span>
      </Button>
      <Collapse in={expanded}>
        <div className={classes.sectionBody} id={id}>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {failing.map((row) => (
              <FailingRow key={row.id} row={row} />
            ))}
            {noData.map((row) => (
              <NoDataRow key={row.id} row={row} />
            ))}
          </ul>
          {passing.length > 0 && (
            <>
              <Button
                size="small"
                className={classes.passToggle}
                aria-expanded={showPassing}
                aria-controls={`${id}-passing`}
                onClick={() => setShowPassing((v) => !v)}
                startIcon={<CheckCircleIcon className={classes.passIcon} />}
              >
                {passing.length} passing ({showPassing ? "hide" : "show"})
              </Button>
              <Collapse in={showPassing}>
                <ul
                  id={`${id}-passing`}
                  style={{ listStyle: "none", margin: 0, padding: 0 }}
                >
                  {passing.map((row) => (
                    <PassingRow key={row.id} row={row} />
                  ))}
                </ul>
              </Collapse>
            </>
          )}
          {section.notAutomated.length > 0 && (
            <NotAutomatedList
              category={section.category}
              rows={section.notAutomated}
              id={id}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

/** The scorecard itself, from an already built model. */
function ServiceScorecardView(props: {
  title: string;
  model: ServiceScorecardModel;
}) {
  const classes = useStyles();
  const { title, model } = props;
  const { hash } = useLocation();
  const linked = categoryAnchorOf(
    hash,
    model.sections.map((s) => s.category)
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      model.sections.map((s) => [
        s.category,
        s.failed > 0 || s.category === linked,
      ])
    )
  );

  // Focusing the toggle also scrolls the linked section into view.
  useEffect(() => {
    if (!linked) return;
    setExpanded((prev) => ({ ...prev, [linked]: true }));
    document.getElementById(`${sectionId(linked)}-toggle`)?.focus();
  }, [linked]);

  if (model.sections.length === 0) {
    return (
      <Paper className={classes.root}>
        <Typography variant="h5">{title}</Typography>
        <Typography className={classes.subtle}>
          No scorecard checks are configured.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" component="h2">
          {title}
        </Typography>
        <Typography variant="h6" component="p">
          {model.passed}/{model.scored} passing
        </Typography>
      </div>
      <Box my={1}>
        <PassBar
          tally={{ passed: model.passed, total: model.scored }}
          label={`Overall: ${model.passed} of ${model.scored} checks passing`}
        />
      </Box>
      <Typography variant="body2" className={classes.subtle} gutterBottom>
        {model.failed > 0 ? `${model.failed} failing` : "Nothing failing"}
        {model.noData > 0 && ` · ${model.noData} with no data yet`}
        {model.lastUpdated && (
          <>
            {" · "}
            <Tooltip
              title={model.sourceUpdated
                .map((s) => `${s.source}: ${s.at.toLocaleString()}`)
                .join("\n")}
            >
              <span>Last updated {relativeTime(model.lastUpdated)}</span>
            </Tooltip>
          </>
        )}
      </Typography>
      {model.sections.map((section) => (
        <Section
          key={section.category}
          section={section}
          expanded={!!expanded[section.category]}
          onToggle={() =>
            setExpanded((prev) => ({
              ...prev,
              [section.category]: !prev[section.category],
            }))
          }
        />
      ))}
    </Paper>
  );
}

/**
 * Service Scorecard tab: Tech Insights checks for the current entity, grouped by
 * category with failures first, the values behind each failure and how to fix it.
 */
export function ServiceScorecard() {
  const { entity } = useEntity();
  const api = useApi(techInsightsApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const standards = useStandards();
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    input?: Omit<Parameters<typeof buildModel>[0], "standards">;
  }>({ loading: true });

  const entityRef = useMemo(() => getCompoundEntityRef(entity), [entity]);
  const githubSlug = entity.metadata.annotations?.[GITHUB_SLUG_ANNOTATION];

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    (async () => {
      const [checks, results, retrievers] = await Promise.all([
        api.getAllChecks(),
        api.runChecks(entityRef),
        loadDeclaredRetrievers({ discoveryApi, fetchApi }),
      ]);
      const retrieverIds = Array.from(
        new Set(checks.flatMap((c) => c.factIds))
      );
      let factRows: FactRows | undefined;
      try {
        factRows = retrieverIds.length
          ? ((await api.getFacts(entityRef, retrieverIds)) as FactRows)
          : {};
      } catch {
        // Timestamps and context values are a nice-to-have.
        factRows = undefined;
      }
      return {
        checks,
        results: results as unknown as TechInsightsCheckResult[],
        factRows,
        githubSlug,
        retrievers,
      };
    })().then(
      (input) => !cancelled && setState({ loading: false, input }),
      (error) => !cancelled && setState({ loading: false, error })
    );
    return () => {
      cancelled = true;
    };
  }, [api, entityRef, githubSlug, discoveryApi, fetchApi]);

  // Rebuilt when the registry arrives, which may be after the checks.
  const model = useMemo(
    () => state.input && buildModel({ ...state.input, standards }),
    [state.input, standards]
  );

  if (state.loading) return <Progress />;
  if (state.error) return <ResponseErrorPanel error={state.error} />;
  return (
    <ServiceScorecardView
      title={entity.metadata.title ?? entity.metadata.name}
      model={model!}
    />
  );
}
